import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import SalonScopedRoute from '../components/auth/SalonScopedRoute';
import MainLayout from '../components/layout/MainLayout';
import LoginForm from '../components/auth/LoginForm';
import TenantSignup from '../pages/TenantSignup';
import Dashboard from '../pages/Dashboard';
import { useAppSelector } from '../hooks/redux';
import { isPlatformOnlySuperAdmin } from '../utils/platformAdmin';

import Customers from '../pages/Customers';
import Appointments from '../pages/Appointments';
import Services from '../pages/Services';
import Packages from '../pages/Packages';
import Staff from '../pages/Staff';
import Inventory from '../pages/Inventory';
import Finance from '../pages/Finance';
import Marketing from '../pages/Marketing';
import Analytics from '../pages/Analytics';
import Membership from '../pages/Membership';
import Inquiries from '../pages/Inquiries';
import WhatsappInbox from '../pages/WhatsappInbox';
import Settings from '../pages/Settings';
import SuperAdmin from '../pages/SuperAdmin';
import PublicMenuPage from '../pages/PublicMenuPage';
import Kiosks from '../pages/Kiosks';

const UnauthorizedPage = () => <div>Unauthorized - You don't have permission to access this page</div>;

const CatchAllRedirect: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const target = isPlatformOnlySuperAdmin(user) ? '/super-admin' : '/dashboard';
  return <Navigate to={target} replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const getDefaultRedirect = () => {
    if (!user) return '/dashboard';

    const role = user.role.toLowerCase();
    if (role === 'super_admin') {
      return '/super-admin';
    }
    return '/dashboard';
  };

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to={getDefaultRedirect()} replace /> : <LoginForm />
        }
      />
      <Route path="/signup" element={<TenantSignup />} />
      <Route path="/menu" element={<PublicMenuPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to={getDefaultRedirect()} replace />} />
        <Route
          path="dashboard"
          element={
            <SalonScopedRoute>
              <Dashboard />
            </SalonScopedRoute>
          }
        />

        <Route
          path="customers"
          element={
            <SalonScopedRoute>
              <Customers />
            </SalonScopedRoute>
          }
        />

        <Route
          path="appointments"
          element={
            <SalonScopedRoute>
              <Appointments />
            </SalonScopedRoute>
          }
        />

        <Route
          path="services"
          element={
            <SalonScopedRoute>
              <Services />
            </SalonScopedRoute>
          }
        />
        <Route
          path="packages"
          element={
            <SalonScopedRoute>
              <Packages />
            </SalonScopedRoute>
          }
        />

        <Route
          path="staff"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Staff />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="inventory"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Inventory />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="finance/*"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Finance />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="inquiries"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Inquiries />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="whatsapp-inbox"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager', 'staff', 'receptionist']}>
                <WhatsappInbox />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="marketing"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Marketing />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="analytics"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Analytics />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="membership"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Membership />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="kiosks"
          element={
            <SalonScopedRoute>
              <ProtectedRoute requiredRoles={['super_admin', 'salon_owner', 'manager']}>
                <Kiosks />
              </ProtectedRoute>
            </SalonScopedRoute>
          }
        />

        <Route
          path="settings"
          element={
            <ProtectedRoute requiredRoles={['super_admin', 'salon_owner']}>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route
          path="super-admin"
          element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <SuperAdmin />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
